/* eslint-disable no-console */
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import React from 'react';

class WidgetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      showDetails: false,
      isRetrying: false,
    };
    
    // Bind methods
    this.handleRetry = this.handleRetry.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.toggleDetails = this.toggleDetails.bind(this);
    this.logError = this.logError.bind(this);
  }

  static getDerivedStateFromError(_error) {
    // Update state to trigger error UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error with enhanced context
    this.logError(error, errorInfo);
  }

  logError(error, errorInfo) {
    const errorReport = {
      widget: this.props.widgetId || 'unknown',
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      retryCount: this.state.retryCount,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Console logging in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Widget Error: ${this.props.widgetId}`);
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Error Report:', errorReport);
      console.groupEnd();
    }

    // Call onError prop if provided
    if (this.props.onError) {
      this.props.onError(errorReport);
    }

    // Future: Send to error reporting service
    // this.sendToErrorReporting(errorReport);
  }

  handleRetry() {
    const { maxRetries = 3 } = this.props;
    
    if (this.state.retryCount >= maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    // Add delay before retry for better UX
    setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
        showDetails: false,
        isRetrying: false,
      }));

      // Call onRetry callback if provided
      if (this.props.onRetry) {
        this.props.onRetry(this.state.retryCount + 1);
      }
    }, 1000);
  }

  handleReset() {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      showDetails: false,
      isRetrying: false,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  }

  toggleDetails() {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  }

  render() {
    if (this.state.hasError) {
      const {
        widgetId,
        widgetTitle,
        compact = false,
        maxRetries = 3,
        showRetryButton = true,
        showResetButton = true,
        fallbackComponent: FallbackComponent,
      } = this.props;

      const { retryCount, isRetrying, showDetails } = this.state;
      const canRetry = retryCount < maxRetries;

      // Custom fallback component if provided
      if (FallbackComponent) {
        return (
          <FallbackComponent 
            error={this.state.error}
            retryCount={retryCount}
            onRetry={canRetry ? this.handleRetry : null}
            onReset={this.handleReset}
            isRetrying={isRetrying}
          />
        );
      }

      // Compact error display for small widgets
      if (compact) {
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Alert 
              severity="error"
              sx={{
                '& .MuiAlert-message': {
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
              }}
              action={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {showRetryButton && canRetry && (
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={this.handleRetry}
                      disabled={isRetrying}
                      aria-label="위젯 다시 로드"
                    >
                      <RefreshIcon 
                        sx={{
                          animation: isRetrying ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }}
                      />
                    </IconButton>
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={this.toggleDetails}
                      aria-label={showDetails ? '오류 세부정보 숨기기' : '오류 세부정보 보기'}
                    >
                      {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </Box>
              }
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {widgetTitle || '위젯'} 로드 오류
                </Typography>
                {retryCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    재시도 {retryCount}/{maxRetries}
                  </Typography>
                )}
              </Box>
            </Alert>

            {/* Development error details */}
            {process.env.NODE_ENV === 'development' && (
              <Collapse in={showDetails}>
                <Box 
                  sx={{ 
                    mt: 1, 
                    p: 1.5, 
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="caption" display="block" gutterBottom>
                    <strong>Error Details:</strong>
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="pre"
                    sx={{ 
                      fontSize: 'inherit',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {this.state.error?.toString()}
                  </Typography>
                </Box>
              </Collapse>
            )}
          </motion.div>
        );
      }

      // Full error display for larger widgets
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              p: 3,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'error.main',
              backgroundColor: 'error.lighter',
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                delay: 0.2,
                type: 'spring',
                stiffness: 200,
                damping: 10,
              }}
            >
              <ErrorIcon 
                sx={{ 
                  fontSize: 48, 
                  color: 'error.main',
                  mb: 2,
                }} 
              />
            </motion.div>
            
            <Typography variant="h6" color="error.main" gutterBottom fontWeight={600}>
              {widgetTitle || '위젯'} 오류
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              {canRetry 
                ? '위젯을 불러오는 중 문제가 발생했습니다.'
                : `최대 재시도 횟수(${maxRetries})를 초과했습니다.`
              }
            </Typography>

            {retryCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                재시도 {retryCount}/{maxRetries}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {showRetryButton && canRetry && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      <RefreshIcon 
                        sx={{
                          animation: isRetrying ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }}
                      />
                    }
                    onClick={this.handleRetry}
                    disabled={isRetrying}
                    color="error"
                  >
                    {isRetrying ? '재시도 중...' : '다시 시도'}
                  </Button>
                </motion.div>
              )}
              
              {showResetButton && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={this.handleReset}
                    color="error"
                  >
                    초기화
                  </Button>
                </motion.div>
              )}
            </Box>

            {/* Development error details */}
            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mt: 2, width: '100%' }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<BugReportIcon />}
                  onClick={this.toggleDetails}
                  color="error"
                >
                  {showDetails ? '오류 세부정보 숨기기' : '오류 세부정보 보기'}
                </Button>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box 
                        sx={{ 
                          mt: 2, 
                          p: 2, 
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          borderRadius: 1,
                          textAlign: 'left',
                          maxHeight: '300px',
                          overflow: 'auto',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                        }}
                      >
                        <Typography variant="caption" display="block" gutterBottom color="inherit">
                          <strong>Widget ID:</strong> {widgetId}
                        </Typography>
                        <Typography variant="caption" display="block" gutterBottom color="inherit">
                          <strong>Error:</strong>
                        </Typography>
                        <Typography 
                          variant="body2" 
                          component="pre"
                          sx={{ 
                            fontSize: 'inherit',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            color: 'inherit',
                            mb: 1,
                          }}
                        >
                          {this.state.error?.toString()}
                        </Typography>

                        {this.state.errorInfo?.componentStack && (
                          <>
                            <Typography variant="caption" display="block" gutterBottom color="inherit">
                              <strong>Component Stack:</strong>
                            </Typography>
                            <Typography 
                              variant="body2" 
                              component="pre"
                              sx={{ 
                                fontSize: 'inherit',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                color: 'inherit',
                              }}
                            >
                              {this.state.errorInfo.componentStack}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            )}
          </Box>
        </motion.div>
      );
    }

    return this.props.children;
  }
}

WidgetErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  widgetId: PropTypes.string,
  widgetTitle: PropTypes.string,
  compact: PropTypes.bool,
  maxRetries: PropTypes.number,
  showRetryButton: PropTypes.bool,
  showResetButton: PropTypes.bool,
  onError: PropTypes.func,
  onRetry: PropTypes.func,
  onReset: PropTypes.func,
  fallbackComponent: PropTypes.elementType,
};

WidgetErrorBoundary.defaultProps = {
  widgetId: 'unknown',
  widgetTitle: null,
  compact: false,
  maxRetries: 3,
  showRetryButton: true,
  showResetButton: true,
  onError: null,
  onRetry: null,
  onReset: null,
  fallbackComponent: null,
};

export default WidgetErrorBoundary;